import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import type { BoardViewportState, WorldBounds } from "../../lib/board/boardViewport";
import {
  panToWorldPoint,
  visibleWorldFromViewport,
} from "../../lib/board/boardViewport";
import { unionBounds, type Bounds } from "../../editor/selectionBounds";
import type { SceneObject } from "../../editor/SceneHost";

const MAP_W = 168;
const MAP_H = 118;
const PAD = 10;
/** Сколько прямоугольников рисуем самое большее (на исписанной доске их тысячи). */
const MAX_OBJECTS = 600;
const EMPTY_WORLD: Bounds = { x: -240, y: -180, w: 480, h: 360 };

type Props = {
  ready: boolean;
  objects: SceneObject[];
  /** Тик сцены — чтобы карта обновлялась после create/delete/move */
  sceneTick?: number;
  viewport: BoardViewportState;
  /**
   * Камера прямо сейчас. React-копия (viewport) во время прокрутки и
   * масштабирования нарочно не обновляется и догоняет только через пару
   * секунд — карта по ней рисовала рамку не там и вела не туда.
   */
  getLiveViewport?: () => BoardViewportState;
  viewportRef: RefObject<HTMLDivElement | null>;
  onViewportChange: (next: BoardViewportState) => void;
  onUserNavigate?: () => void;
};

type MapTransform = {
  s: number;
  ox: number;
  oy: number;
  world: Bounds;
};

function unionWorld(a: Bounds, b: Bounds): Bounds {
  const minX = Math.min(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxX = Math.max(a.x + a.w, b.x + b.w);
  const maxY = Math.max(a.y + a.h, b.y + b.h);
  return { x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY) };
}

function expandBounds(b: Bounds, padWorld: number): Bounds {
  return {
    x: b.x - padWorld,
    y: b.y - padWorld,
    w: b.w + padWorld * 2,
    h: b.h + padWorld * 2,
  };
}

function buildTransform(world: Bounds): MapTransform {
  const bw = Math.max(1, world.w);
  const bh = Math.max(1, world.h);
  const availW = MAP_W - PAD * 2;
  const availH = MAP_H - PAD * 2;
  const s = Math.min(availW / bw, availH / bh);
  const ox = PAD + (availW - bw * s) / 2;
  const oy = PAD + (availH - bh * s) / 2;
  return { s, ox, oy, world };
}

function worldToMap(t: MapTransform, x: number, y: number): { x: number; y: number } {
  return {
    x: t.ox + (x - t.world.x) * t.s,
    y: t.oy + (y - t.world.y) * t.s,
  };
}

function mapToWorld(t: MapTransform, mx: number, my: number): { x: number; y: number } {
  return {
    x: t.world.x + (mx - t.ox) / t.s,
    y: t.world.y + (my - t.oy) / t.s,
  };
}

/**
 * Мини-карта: обзор доски + рамка видимой области.
 * Клик / drag — центрирует камеру в этой точке (масштаб не меняет).
 */
export function BoardMinimap({
  ready,
  objects,
  sceneTick = 0,
  viewport: viewportProp,
  getLiveViewport,
  viewportRef,
  onViewportChange,
  onUserNavigate,
}: Props) {
  // Перерисовка карты вслед за камерой — не чаще кадра.
  const [, setLiveTick] = useState(0);
  useEffect(() => {
    if (!getLiveViewport) return;
    let raf = 0;
    const onLive = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setLiveTick((n) => n + 1);
      });
    };
    window.addEventListener("wb:live-viewport", onLive);
    return () => {
      window.removeEventListener("wb:live-viewport", onLive);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [getLiveViewport]);
  const viewport = getLiveViewport ? getLiveViewport() : viewportProp;
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef(false);

  const content = useMemo(() => {
    const u = unionBounds(objects);
    if (!u || u.w < 8 || u.h < 8) return EMPTY_WORLD;
    return u;
    // sceneTick: список объектов меняется на месте, и одной ссылки мало —
    // без счётчика карта держала бы старые места после перемещений.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, sceneTick]);

  const viewportSize = (() => {
    const el = viewportRef.current;
    if (!el) return { w: 800, h: 600 };
    const r = el.getBoundingClientRect();
    return { w: Math.max(8, r.width), h: Math.max(8, r.height) };
  })();

  const viewWorld = visibleWorldFromViewport(viewportSize.w, viewportSize.h, viewport);

  const mapWorld = useMemo(() => {
    const merged = expandBounds(unionWorld(content, viewWorld), Math.max(content.w, content.h) * 0.04);
    return merged;
  }, [content, viewWorld]);

  const transform = useMemo(() => buildTransform(mapWorld), [mapWorld]);

  const objectRects = useMemo(() => {
    // Раньше брались первые 120 объектов, то есть самые старые: на исписанной
    // доске свежие записи на карте не появлялись вовсе, а «скопления» на ней
    // показывали давно ушедшее. Теперь — рамки целиком и равномерная выборка
    // со всей доски.
    let list = objects;
    if (objects.length > MAX_OBJECTS) {
      const frames = objects.filter((o) => o.type === "frame");
      const rest = objects.filter((o) => o.type !== "frame");
      const step = rest.length / Math.max(1, MAX_OBJECTS - frames.length);
      const sampled: SceneObject[] = [];
      for (let i = 0; i < rest.length && sampled.length < MAX_OBJECTS - frames.length; i += step) {
        sampled.push(rest[Math.floor(i)]!);
      }
      list = [...frames, ...sampled];
    }
    return list
      .map((o) => {
        const w = typeof o.w === "number" ? o.w : 0;
        const h = typeof o.h === "number" ? o.h : 0;
        if (w < 1 && h < 1) return null;
        const tl = worldToMap(transform, o.x, o.y);
        const br = worldToMap(transform, o.x + Math.max(w, 4), o.y + Math.max(h, 4));
        return {
          id: o.id,
          x: tl.x,
          y: tl.y,
          w: Math.max(2, br.x - tl.x),
          h: Math.max(2, br.y - tl.y),
          frame: o.type === "frame",
        };
      })
      .filter(Boolean) as {
      id: string;
      x: number;
      y: number;
      w: number;
      h: number;
      frame: boolean;
    }[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, transform, sceneTick]);

  const viewRect = useMemo(() => {
    const tl = worldToMap(transform, viewWorld.x, viewWorld.y);
    const br = worldToMap(transform, viewWorld.x + viewWorld.w, viewWorld.y + viewWorld.h);
    return {
      x: tl.x,
      y: tl.y,
      w: Math.max(4, br.x - tl.x),
      h: Math.max(4, br.y - tl.y),
    };
  }, [transform, viewWorld]);

  const goToClient = useCallback(
    (clientX: number, clientY: number) => {
      const el = rootRef.current;
      const vpEl = viewportRef.current;
      if (!el || !vpEl) return;
      const rect = el.getBoundingClientRect();
      const mx = clientX - rect.left;
      const my = clientY - rect.top;
      const world = mapToWorld(transform, mx, my);
      const { width, height } = vpEl.getBoundingClientRect();
      onUserNavigate?.();
      onViewportChange(panToWorldPoint(width, height, viewport, world.x, world.y));
    },
    [transform, viewport, viewportRef, onViewportChange, onUserNavigate]
  );

  const onPointerDown = useCallback(
    (ev: React.PointerEvent) => {
      if (ev.button !== 0) return;
      ev.preventDefault();
      ev.stopPropagation();
      dragRef.current = true;
      try {
        (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
      } catch {
        /* перо или палец без захвата — перейти всё равно надо */
      }
      goToClient(ev.clientX, ev.clientY);
    },
    [goToClient]
  );

  const onPointerMove = useCallback(
    (ev: React.PointerEvent) => {
      if (!dragRef.current) return;
      goToClient(ev.clientX, ev.clientY);
    },
    [goToClient]
  );

  const endDrag = useCallback(() => {
    dragRef.current = false;
  }, []);

  if (!ready) return null;

  return (
    <div
      ref={rootRef}
      className="wb-minimap"
      role="application"
      aria-label="Мини-карта доски. Кликните, чтобы перейти"
      title="Мини-карта — клик или перетаскивание"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <svg width={MAP_W} height={MAP_H} viewBox={`0 0 ${MAP_W} ${MAP_H}`} aria-hidden>
        <rect className="wb-minimap__bg" x="0" y="0" width={MAP_W} height={MAP_H} rx="8" />
        {objectRects.map((r) => (
          <rect
            key={r.id}
            className={r.frame ? "wb-minimap__obj wb-minimap__obj--frame" : "wb-minimap__obj"}
            x={r.x}
            y={r.y}
            width={r.w}
            height={r.h}
            rx="1"
          />
        ))}
        <rect
          className="wb-minimap__view"
          x={viewRect.x}
          y={viewRect.y}
          width={viewRect.w}
          height={viewRect.h}
        />
      </svg>
    </div>
  );
}

/** Bounds всего контента для Fit to content (с запасом). */
export function contentBoundsForFit(objects: SceneObject[]): WorldBounds | null {
  const u = unionBounds(objects);
  if (!u || (u.w < 1 && u.h < 1)) return null;
  const pad = Math.max(40, Math.max(u.w, u.h) * 0.04);
  return expandBounds(
    { x: u.x, y: u.y, w: Math.max(u.w, 80), h: Math.max(u.h, 80) },
    pad
  );
}
